import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import { calculateTotalPages } from "../../../utils/Helper";
import {
  FeatureCatalogListAction,
  FeatureCatalogCreateAction,
  FeatureCatalogUpdateAction,
  FeatureCatalogDeleteAction,
} from "../../../Redux/Actions/PricingAction";
import {
  PricingPageHeader,
  StatusChip,
  pricingBodyRowSx,
  pricingTableContainerSx,
  pricingTableHeadSx,
} from "../shared/pricingHelpers";
import FeatureCatalogForm, {
  FeatureCatalogValues,
} from "./FeatureCatalogForm/FeatureCatalogForm";

const ROWS_PER_PAGE = 10;

const FeatureCatalog: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { rows = [], loading } = useSelector(
    (state: any) => state.FeatureCatalogListData || {}
  );

  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchList = () => dispatch(FeatureCatalogListAction());

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paged = useMemo(
    () => rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [rows, page]
  );

  const openCreate = () => {
    setMode("create");
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: any) => {
    setMode("edit");
    setEditing(row);
    setFormOpen(true);
  };

  const initialFromRow = (row: any): Partial<FeatureCatalogValues> | null =>
    row
      ? {
          feature_key: row.feature_key ?? "",
          display_name: row.display_name ?? "",
          feature_group: row.feature_group ?? "",
          display_price_usd: row.display_price_usd ?? "",
          display_price_inr: row.display_price_inr ?? "",
          credit_charge: row.credit_charge ?? "",
          calculator_visible: row.calculator_visible ?? true,
          display_order: row.display_order ?? 0,
          is_active: row.is_active ?? true,
          msc_feature_id: row.msc_feature_id ?? "",
          msc_action_event: row.msc_action_event ?? "",
          msc_content_type_id: row.msc_content_type_id ?? "",
          msc_external_service_resource_id:
            row.msc_external_service_resource_id ?? "",
          msc_unit_type: row.msc_unit_type ?? "ACTION",
          msc_charge_status: row.msc_charge_status ?? "ACTIVE",
        }
      : null;

  const handleSubmit = async (payload: any) => {
    setSaving(true);
    const res =
      mode === "create"
        ? await dispatch(FeatureCatalogCreateAction(payload))
        : await dispatch(FeatureCatalogUpdateAction(editing.id, payload));
    setSaving(false);
    if (res?.status === true) {
      setFormOpen(false);
      fetchList();
    }
  };

  const handleDelete = (row: any) => {
    Swal.fire({
      title: "Delete feature?",
      text: `"${row.display_name}" will be removed from the catalog.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d92d20",
      cancelButtonColor: "#667085",
      confirmButtonText: "Delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const res = await dispatch(FeatureCatalogDeleteAction(row.id));
        if (res?.status === true) fetchList();
      }
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Feature Catalog"
            breadcrumbs={[
              { label: "Pricing", path: "/pricing/feature-catalog", isActive: false },
              { label: "Feature Catalog", path: "/pricing/feature-catalog", isActive: true },
            ]}
          />
        </div>

        <PricingPageHeader
          title="Feature Catalog"
          subtitle="Chargeable features shown on the pricing page and calculator."
          actionLabel="Add Feature"
          onAction={openCreate}
        />

        <Box sx={{ mx: 2, mb: 2 }}>
          <TableContainer component={Paper} sx={pricingTableContainerSx}>
            <Table aria-label="Feature Catalog">
              <TableHead sx={pricingTableHeadSx}>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Feature Key</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align="right">USD</TableCell>
                  <TableCell align="right">INR</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell align="center">Calc.</TableCell>
                  <TableCell align="right">Order</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length > 0 ? (
                  paged.map((row: any, idx: number) => (
                    <TableRow key={row.id} sx={pricingBodyRowSx}>
                      <TableCell>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {row.feature_key}
                      </TableCell>
                      <TableCell>{row.display_name}</TableCell>
                      <TableCell>{row.feature_group}</TableCell>
                      <TableCell align="right">{row.display_price_usd}</TableCell>
                      <TableCell align="right">
                        {row.display_price_inr ?? "—"}
                      </TableCell>
                      <TableCell align="right">{row.credit_charge}</TableCell>
                      <TableCell align="center">
                        {row.calculator_visible ? "Yes" : "No"}
                      </TableCell>
                      <TableCell align="right">{row.display_order}</TableCell>
                      <TableCell align="center">
                        <StatusChip active={Boolean(row.is_active)} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                            sx={{ color: "#d92d20" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, color: "#667085" }}>
                      No features found. Click “Add Feature” to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {rows.length > ROWS_PER_PAGE && (
          <div className="pagination-style">
            <Stack spacing={2}>
              <Pagination
                count={calculateTotalPages(rows.length, ROWS_PER_PAGE)}
                page={page}
                onChange={(_e, p) => setPage(p)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Stack>
          </div>
        )}
      </div>

      <FeatureCatalogForm
        open={formOpen}
        mode={mode}
        initial={initialFromRow(editing)}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
      <ToastDefault />
    </>
  );
};

export default FeatureCatalog;
